import Gender from "../enums/gender.enum"
import moment from 'moment'
import { message } from 'antd'
import copy from 'copy-to-clipboard'
import { addImpersonation } from '../services/impersonation.service.js';
import MembershipHelper from "./membership.helper.js";
import MembershipTypeCode from "../enums/membershipTypeCode.enum.js";
import { listCards } from '../services/card.service.js';
import MedicalCondition from "../enums/medicalCondition.enum";
import Disqualification from "../enums/disqualification.enum";

const PatientHelper = {
  getAge: (patient) => {
    if (!patient) return null
    const { dob } = patient
    if (!dob) return null
    return moment().diff(moment(dob, 'MM/DD/YYYY'), 'years')
  },
  isFemale: (patient) => {
    return patient?.gender === Gender.FEMALE
  },
  isMale: (patient) => {
    return patient?.gender === Gender.MALE
  },
  isShort: (patient) => {
    return patient && patient.height < 60
  },
  isAthlete: (patient) => {
    return patient && patient.isAthlete
  },
  onBirthControl: (patient) => {
    return patient?.birthControl !== undefined && patient?.birthControl.length>0
  },
  getFirstAppointment: (user) => {
    if (!user) return null
    let { appointments } = user
    if (!appointments?.length) return null
    const appointmentsByStart = appointments.sort((a, b) => {
      return new Date(a.start).getTime() - new Date(b.start).getTime()
    })
    return appointmentsByStart[0]
  },
  onImpersonate:  async (user) => {
    try {
      const impersonate = await addImpersonation({ user: user })
      copy(`${process.env.REACT_APP_CLIENT_URL}/impersonate/${impersonate._id}`)
      message.success('Copied login link')
    } catch (err) {
      message.error('Failed to generate login link')   
    }
  },
  getWeight: async (user) => {
    const { weight } = user
    return weight
  },
  isInstalabMember: async(patient) => {
    return MembershipHelper.getActiveMembership(patient.memberships, MembershipTypeCode.LONGEVITY)
      || MembershipHelper.getActiveMembership(patient.memberships, MembershipTypeCode.ULTIMATE)
      || MembershipHelper.getActiveMembership(patient.memberships, MembershipTypeCode.PREMIUM)
      || MembershipHelper.getActiveMembership(patient.memberships, MembershipTypeCode.PREMIUM_12MO)
      || MembershipHelper.getActiveMembership(patient.memberships, MembershipTypeCode.LONGEVITY_12MO)
      || MembershipHelper.getActiveMembership(patient.memberships, MembershipTypeCode.HEART_HEALTH_V2)
  },
  isHeartHealthMember: async(patient) => {
    return MembershipHelper.getActiveMembership(patient.memberships, MembershipTypeCode.HEART_HEALTH) 
      || MembershipHelper.getActiveMembership(patient.memberships, MembershipTypeCode.HEART_HEALTH_V2)
      || MembershipHelper.getActiveMembership(patient.memberships, MembershipTypeCode.ULTIMATE)
  },

  isUltimateMember: async(patient) => {
    return MembershipHelper.getActiveMembership(patient.memberships, MembershipTypeCode.ULTIMATE)
  },
  hasCredit: (patient, productTypeCode) => {
    if (!patient) return false
    const { credits } = patient
    if (!credits?.length) return false
    const hasCredit = credits.find(credit => credit === productTypeCode)
    return hasCredit
  },
  hasActiveCard: async (patient) => {
    const cards = await listCards({
      filter: { isRemoved: false }
    })
    return cards.length > 0
  },
  metforminQualified: (patient) => {
    if (!patient) return null;

    const {
      dob,
      isPregnant,
      isBreastfeeding,
      currentMedicalConditions = [],
      cholesterolAdverseReaction,
      hasPriorMetforminUse,
      alcoholQty,
      glucose,
      hba1c,
    } = patient;


    let disqualifications = [];

    // Immediate disqualifications: use arrays for batch checking
    const immediateDisqualifications = [
      [
        moment(dob).isAfter(moment().subtract(18, 'years')),
        Disqualification.UNDER_18
      ],
      [isPregnant, Disqualification.PREGNANT],
      [isBreastfeeding, Disqualification.BREASTFEEDING],
      [currentMedicalConditions.includes(MedicalCondition.END_STAGE_KIDNEY_DISEASE), Disqualification.END_STAGE_KIDNEY_DISEASE],
      [currentMedicalConditions.includes(MedicalCondition.CHRONIC_KIDNEY_DISEASE), Disqualification.CHRONIC_KIDNEY_DISEASE],
      [currentMedicalConditions.includes(MedicalCondition.END_STAGE_LIVER_DISEASE), Disqualification.END_STAGE_LIVER_DISEASE],
      [currentMedicalConditions.includes(MedicalCondition.CIRROHOSIS), Disqualification.CIRROHOSIS],
      [currentMedicalConditions.includes(MedicalCondition.LACTIC_ACIDOSIS), Disqualification.LACTIC_ACIDOSIS],
      [currentMedicalConditions.includes(MedicalCondition.HEART_FAILURE), Disqualification.HEART_FAILURE],
      [hasPriorMetforminUse && cholesterolAdverseReaction.metformin, Disqualification.METFORMIN_ADVERSE_REACTION],
      [alcoholQty && Number(alcoholQty) > 14, Disqualification.HEAVY_ALCOHOL_CONSUMPTION],
      [glucose && Number(glucose) < 70, Disqualification.LOW_GLUCOSE],
      [hba1c && Number(hba1c) < 4.8, Disqualification.LOW_HBA1C],
    ];

    immediateDisqualifications.forEach(([condition, reason]) => {
      if (condition) disqualifications.push(reason);
    });

    if (disqualifications.length > 0) return { qualified: false, disqualifications };

    
    return {
      qualified: true,
      disqualifications: []
    }
    
  },
  ezetimibeQualified: (patient) => {
    if (!patient) return null;

    const {
      dob,
      isPregnant,
      isBreastfeeding,
      currentMedicalConditions = [],
      cholesterolAdverseReaction,
      hasPriorEzetimibeUse,
    
    } = patient;


    let disqualifications = [];

    // Immediate disqualifications: use arrays for batch checking
    const immediateDisqualifications = [
      [
        moment(dob).isAfter(moment().subtract(18, 'years')),
        Disqualification.UNDER_18
      ],
      [isPregnant, Disqualification.PREGNANT],
      [isBreastfeeding, Disqualification.BREASTFEEDING],
      [currentMedicalConditions.includes(MedicalCondition.END_STAGE_KIDNEY_DISEASE), Disqualification.END_STAGE_KIDNEY_DISEASE],
      [currentMedicalConditions.includes(MedicalCondition.END_STAGE_LIVER_DISEASE), Disqualification.END_STAGE_LIVER_DISEASE],
      [currentMedicalConditions.includes(MedicalCondition.CIRROHOSIS), Disqualification.CIRROHOSIS],
      [hasPriorEzetimibeUse && cholesterolAdverseReaction.ezetimibe, Disqualification.EZETIMIBE_ADVERSE_REACTION],
    ];

    immediateDisqualifications.forEach(([condition, reason]) => {
      if (condition) disqualifications.push(reason);
    });

    if (disqualifications.length > 0) return { qualified: false, disqualifications };

    
    return {
      qualified: true,
      disqualifications: []
    }
    
  },
  statinQualified: (patient) => {
    if (!patient) return null;

    const {
      dob,
      isPregnant,
      isBreastfeeding,
      currentMedicalConditions = [],
      cholesterolAdverseReaction,
      hasPriorStatinUse,
    
    } = patient;


    let disqualifications = [];

    // Immediate disqualifications: use arrays for batch checking
    const immediateDisqualifications = [
      [
        moment(dob).isAfter(moment().subtract(18, 'years')),
        Disqualification.UNDER_18
      ],
      [isPregnant, Disqualification.PREGNANT],
      [isBreastfeeding, Disqualification.BREASTFEEDING],
      [currentMedicalConditions.includes(MedicalCondition.END_STAGE_KIDNEY_DISEASE), Disqualification.END_STAGE_KIDNEY_DISEASE],
      [currentMedicalConditions.includes(MedicalCondition.END_STAGE_LIVER_DISEASE), Disqualification.END_STAGE_LIVER_DISEASE],
      [currentMedicalConditions.includes(MedicalCondition.CIRROHOSIS), Disqualification.CIRROHOSIS],
      [hasPriorStatinUse && cholesterolAdverseReaction.statins, Disqualification.STATIN_ADVERSE_REACTION],
    ];

    immediateDisqualifications.forEach(([condition, reason]) => {
      if (condition) disqualifications.push(reason);
    });

    if (disqualifications.length > 0) return { qualified: false, disqualifications };

    
    return {
      qualified: true,
      disqualifications: []
    }
    
  },
  cctaQualified: (patient) => {
    if (!patient) return null;

    const {
      isPregnant,
      hasContrastDyeAllergy,
      currentMedicalConditions = [],
      allMedicalConditions = [],
      familyHistory = [],
      hasChestPain,
      hasRecentIodineTreatment,
      heartAttackDate,
      holdBreath,
      hasPastSmoking,
      hasCurrentSmoking
    } = patient;

    let disqualifications = [];

    // Immediate disqualifications: use arrays for batch checking
    const immediateDisqualifications = [
      [isPregnant, Disqualification.PREGNANT],
      [hasRecentIodineTreatment, Disqualification.RECENT_IODINE_TREATMENT],
      [hasContrastDyeAllergy, Disqualification.CONTRAST_DYE_ALLERGY],
      [!holdBreath, Disqualification.HOLD_BREATH],
      [
        heartAttackDate && moment(heartAttackDate).isAfter(moment().subtract(1, 'month')),
        Disqualification.RECENT_HEART_ATTACK
      ]
    ];


    immediateDisqualifications.forEach(([condition, reason]) => {
      if (condition) disqualifications.push(reason);
    });

    // Disqualifying medical conditions
    const disqualifyingConditions = [
      [MedicalCondition.ARRHYTHMIA, Disqualification.ARRHYTHMIA],
      [MedicalCondition.TACHYCARDIA, Disqualification.TACHYCARDIA],
      [MedicalCondition.HEART_BLOCK, Disqualification.HEART_BLOCK],
      [MedicalCondition.HEART_FAILURE, Disqualification.HEART_FAILURE],
      [MedicalCondition.COPD, Disqualification.COPD],
      [MedicalCondition.ASTHMA, Disqualification.ASTHMA],
      [MedicalCondition.CHRONIC_KIDNEY_DISEASE, Disqualification.CHRONIC_KIDNEY_DISEASE]
    ];

    disqualifyingConditions.forEach(([condition, reason]) => {
      if (currentMedicalConditions.includes(condition)) disqualifications.push(reason);
    });

    if (disqualifications.length > 0) return { qualified: false, disqualifications };

    // Immediate qualifications
    const age = PatientHelper.getAge(patient);

    if (
      (age >= 45 && (PatientHelper.isMale(patient) || (age >= 55 && PatientHelper.isFemale(patient)))) ||
      hasChestPain ||
      hasPastSmoking ||
      hasCurrentSmoking ||
      allMedicalConditions.some(cond =>
        [
          MedicalCondition.BYPASS_SURGERY,
          MedicalCondition.POSITIVE_CAC,
          MedicalCondition.HEART_CATHERTERIZATION,
          MedicalCondition.ABNORMAL_STRESS_TEST
        ].includes(cond)
      ) ||
      currentMedicalConditions.some(cond =>
        [
          MedicalCondition.HIGH_BLOOD_PRESSURE,
          MedicalCondition.HIGH_CHOLESTEROL,
          MedicalCondition.DIABETES
        ].includes(cond)
      ) ||
      familyHistory.some(cond =>
        [
          MedicalCondition.HEART_ATTACK,
          MedicalCondition.HEART_DISEASE,
          MedicalCondition.STROKE,
          MedicalCondition.PERIPHERAL_ARTERY_DISEASE
        ].includes(cond)
      )
    ) {
      return { qualified: true };
    }

    return { qualified: false, disqualifications: [Disqualification.YOUNG_NO_RISK_FACTORS] };
  }
}

export default PatientHelper
